import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Airplane, ClockCounterClockwise, FileText, Timer } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  APP_PATHS,
  appPath,
  SERVICE_REQUEST_TABS,
  SERVICE_REQUEST_TYPES,
  TEAM_ATTENDANCE_TABS,
  TEAM_TIME_OFF_TABS,
} from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import { useTeamPendingNocCount } from '@/modules/service-requests/noc/hooks/use-noc';
import { useCorrectionQueueCounts } from '@/modules/time-attendance/team-attendance/hooks/use-manage-attendance';
import { EMPTY_MANAGE_ATTENDANCE_FILTERS } from '@/modules/time-attendance/team-attendance/types/manage-attendance.types';
import { buildCorrectionFilter } from '@/modules/time-attendance/team-attendance/utils/manage-attendance-filter';
import {
  leaveScopeFilter,
  useLeaveQueueCounts,
} from '@/modules/time-off/team-time-off/hooks/use-leave-manager';
import { toilScopeFilter } from '@/modules/time-off/team-toil/components/team-toil-tab/team-toil-tab';
import { useToilQueueCounts } from '@/modules/time-off/team-toil/hooks/use-toil-manager';
import { HomeCard, HomeList, HomeListRow } from '../home-card/home-card';

const PERM = HRM_PERMISSIONS;

/** Which review queues the user holds (the same permissions as the team pages' tabs). */
export const useReviewQueues = () => ({
  leave: useHasPermission([PERM.TIME_OFF.VIEW_TEAM, PERM.TIME_OFF.LEAVE_VIEW_ALL], 'all'),
  toil: useHasPermission([PERM.TIME_OFF.VIEW_TEAM, PERM.TIME_OFF.TOIL_VIEW_ALL], 'all'),
  corrections: useHasPermission(
    [PERM.TIME_ATTENDANCE.VIEW_TEAM_ATTENDANCE, PERM.TIME_ATTENDANCE.ATTENDANCE_REVIEW_CORRECTION],
    'all'
  ),
  noc: useHasPermission(
    [PERM.SERVICE_REQUEST.VIEW_TEAM, PERM.SERVICE_REQUEST.NOC_VIEW_REQUESTS],
    'all'
  ),
});

type Row = { key: string; icon: ReactNode; label: string; count?: number; to: string };

/**
 * Pending approvals (Component Map › Pending approvals; agreed 2 Nov 2026): a row per queue the user may
 * review, "N waiting" with Review opening that team page. The counts are the team pages' tab badges.
 * Only rendered for those who hold at least one queue.
 */
export const PendingApprovalsCard = ({
  queues,
}: {
  queues: ReturnType<typeof useReviewQueues>;
}) => {
  const { t } = useTranslation();
  const { getDepartmentIdCondition, isLoading: isScopeLoading } = useDepartmentScope();
  const scope = getDepartmentIdCondition('');

  const leave = useLeaveQueueCounts(
    leaveScopeFilter(scope),
    queues.leave && !isScopeLoading
  ).Pending;
  const toil = useToilQueueCounts(toilScopeFilter(scope), queues.toil && !isScopeLoading).Pending;
  const corrections = useCorrectionQueueCounts(
    buildCorrectionFilter(EMPTY_MANAGE_ATTENDANCE_FILTERS, scope, undefined),
    queues.corrections && !isScopeLoading
  ).Pending;
  const noc = useTeamPendingNocCount(queues.noc);

  const rows = (
    [
      queues.leave && {
        key: 'leave',
        icon: <Airplane />,
        label: t('HOME_LEAVE_REQUESTS'),
        count: leave,
        to: `${APP_PATHS.TEAM_TIME_OFF}?tab=${TEAM_TIME_OFF_TABS.REQUESTS}`,
      },
      queues.toil && {
        key: 'toil',
        icon: <Timer />,
        label: t('TOIL_REQUESTS'),
        count: toil,
        to: `${APP_PATHS.TEAM_TIME_OFF}?tab=${TEAM_TIME_OFF_TABS.TOIL}`,
      },
      queues.corrections && {
        key: 'corrections',
        icon: <ClockCounterClockwise />,
        label: t('HOME_ATTENDANCE_CORRECTIONS'),
        count: corrections,
        to: `${APP_PATHS.TEAM_ATTENDANCE}?tab=${TEAM_ATTENDANCE_TABS.CORRECTIONS}`,
      },
      queues.noc && {
        key: 'noc',
        icon: <FileText />,
        label: t('HOME_NOC_REQUESTS'),
        count: noc,
        to: appPath.serviceRequests(SERVICE_REQUEST_TYPES.NOC, SERVICE_REQUEST_TABS.TEAM),
      },
    ] as (Row | false)[]
  ).filter((row): row is Row => Boolean(row));

  const total = rows.reduce((sum, row) => sum + (row.count ?? 0), 0);

  return (
    <HomeCard title={t('HOME_PENDING_APPROVALS')} count={total}>
      <HomeList emptyText={t('HOME_NOTHING_WAITING')}>
        {rows.map((row) => (
          <HomeListRow
            key={row.key}
            icon={row.icon}
            title={row.label}
            meta={row.count === undefined ? '…' : t('HOME_N_WAITING', { count: row.count })}
            end={
              <Button asChild size="sm" variant="outline">
                <Link to={row.to}>{t('HOME_REVIEW')}</Link>
              </Button>
            }
          />
        ))}
      </HomeList>
    </HomeCard>
  );
};
